import classNames from 'classnames';
import dayjs from 'dayjs';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import React, { ReactNode } from 'react';
import { Table, TableBody, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { LottieWaveDotLoader } from 'Views/Components/_HumanWritten/Lottie/LottieWaveDotLoader';
import If from 'Views/Components/If/If';
import Pagination from 'Views/Components/Pagination/Pagination';

export interface Column {
	key: string;
	label: string;
	headerComponent?: ReactNode; // For custom headers like sortable buttons
	width?: string;
	collapsing?: boolean;
	className?: string;
}

export interface GenericStaffCollectionProps<T> {
	/**
	 * Array of data items to display
	 */
	items: T[] | null;

	/**
	 * Total count of items (for pagination display). Undefined while it is still being counted —
	 * the staff tables fetch the total separately from the page, so rows can arrive first.
	 */
	totalItemCount: number | undefined;

	/**
	 * Items per page
	 */
	perPage: number;

	/**
	 * Current page number
	 */
	currentPage: number;

	/**
	 * Callback when page changes
	 */
	onPageChange: (pageNo: number) => void;

	/**
	 * Is data currently loading?
	 */
	isLoading: boolean;

	/**
	 * Set of selected item IDs
	 */
	selectedItemIds: Set<string>;

	/**
	 * Callback to update selected items
	 */
	setSelectedItemIds: (ids: Set<string>) => void;

	/**
	 * Last time data was updated
	 */
	lastUpdated: Date;

	/**
	 * CSS class name for the container
	 */
	className?: string;

	/**
	 * Function to render each row
	 * Receives: item, isChecked, onToggleCheckbox
	 */
	renderRow: (item: T, isChecked: boolean, onToggleCheckbox: () => void) => ReactNode;

	/**
	 * Column definitions for table headers
	 */
	columns: Column[];

	/**
	 * Message to display when no items found
	 */
	emptyMessage?: string;

	/**
	 * Message to display while loading
	 */
	loadingMessage?: string;

	/**
	 * Identifier to extract from items (default: 'id')
	 */
	idField?: string;

	/**
	 * data-test-id applied to the table element
	 */
	testId?: string;
}

/**
 * Generic reusable staff collection/table component
 * Handles: pagination, loading states, checkbox selection, responsive design
 *
 * Usage:
 * <GenericStaffCollection
 *   items={bookings}
 *   totalItemCount={bookingCount}
 *   perPage={10}
 *   currentPage={filters.pageNo}
 *   onPageChange={(page) => setFilters({...filters, pageNo: page})}
 *   isLoading={loading}
 *   selectedItemIds={selectedBookingIds}
 *   setSelectedItemIds={setSelectedBookingIds}
 *   lastUpdated={lastUpdated}
 *   renderRow={(booking, isChecked, toggle) => <BookingRender booking={booking} isChecked={isChecked} onToggle={toggle} />}
 *   columns={[
 *     { key: 'checkbox', label: '' },
 *     { key: 'name', label: 'Name', headerComponent: <SortableHeader /> },
 *     { key: 'amount', label: 'Amount' },
 *   ]}
 * />
 */
export function GenericStaffCollection<T extends { [key: string]: any }>({
	items,
	totalItemCount,
	perPage,
	currentPage,
	onPageChange,
	isLoading,
	selectedItemIds,
	setSelectedItemIds,
	lastUpdated,
	className,
	renderRow,
	columns,
	emptyMessage = 'No items found',
	loadingMessage = 'Loading...',
	idField = 'id',
	testId,
}: GenericStaffCollectionProps<T>) {
	const { isMobile } = useDeviceDetect();
	const recordCountLabel = totalItemCount === undefined ? 'Counting records…' : `${totalItemCount} records`;

	// Generate row components
	const rowComponents: ReactNode[] = [];
	if (items !== null) {
		items.forEach(item => {
			const itemId = item[idField]?.toString();
			if (!itemId) {
				console.warn(`Item missing ${idField} field:`, item);
				return;
			}

			const isChecked = selectedItemIds.has(itemId);
			rowComponents.push(
				renderRow(item, isChecked, () => {
					const newSet = new Set(selectedItemIds);
					if (newSet.has(itemId)) {
						newSet.delete(itemId);
					} else {
						newSet.add(itemId);
					}
					setSelectedItemIds(newSet);
				}),
			);
		});
	}

	return (
		<div className={classNames('generic-staff-collection', className)}>
			<Table className="fh-table" data-test-id={testId}>
				<TableHeader>
					<TableRow>
						{/* Data column headers */}
						{columns.map(column => (
							<TableHeaderCell
								key={column.key}
								collapsing={column.collapsing}
								className={column.className}
								style={column.width ? { width: column.width } : undefined}
							>
								{column.headerComponent || column.label}
							</TableHeaderCell>
						))}
					</TableRow>
				</TableHeader>

				<TableBody className={classNames({ blur: isLoading })}>{rowComponents}</TableBody>
			</Table>

			{/* Loading state */}
			<If condition={isLoading && (items === null || rowComponents.length === 0)}>
				<p className="staff-collection__no-items">
					<LottieWaveDotLoader />
					<div className="loader-container">
						<div className="loading-text dots">{loadingMessage}</div>
					</div>
				</p>
			</If>

			{/* Empty state */}
			<If condition={!isLoading && rowComponents.length === 0}>
				<p className="staff-collection__no-items">{emptyMessage}</p>
			</If>

			{/* Pagination and footer */}
			<If condition={rowComponents.length > 0}>
				<div className="collection-component">
					{/* Loading overlay */}
					<If condition={isLoading}>
						<div className="loader__overlay">
							<LottieWaveDotLoader className="overlay" />
							<div className="loader-container">
								<div className="loading-text dots">{loadingMessage}</div>
							</div>
						</div>
					</If>

					{/* Footer */}
					<div className="mobile-footer">
						<p className="grey">
							Last updated {formatStandardTime(lastUpdated)}, {dayjs(lastUpdated).format('DD MMM, YYYY')}
						</p>
						<If condition={isMobile}>
							<p className="grey">{recordCountLabel}</p>
						</If>
					</div>

					{/* Pagination — hidden until the total is known, since it renders "page X of Y" */}
					<If condition={totalItemCount !== undefined}>
						<section className="collection__pagination new-style">
							<section className="collection__load">
								<Pagination
									perPage={perPage}
									pageNo={currentPage}
									totalRecords={totalItemCount ?? 0}
									onPageChange={onPageChange}
									showGoToPageBox={false}
									showFirstArrow={false}
									showLastArrow={false}
									leftArrowIcon="arrow-left"
									rightArrowIcon="arrow-right"
								/>
							</section>
						</section>
					</If>

					{/* Desktop record count */}
					<If condition={!isMobile}>
						<p className="grey">{recordCountLabel}</p>
					</If>
				</div>
			</If>
		</div>
	);
}
